<script setup lang="ts">
import { readItems, readUsers } from '@directus/sdk'
import type { CommandPaletteGroup, CommandPaletteItem } from '@nuxt/ui'
import type { DirectusUser } from '~~/types'
import type { GameVehicleSummary } from '~~/types/gamedata'

interface AmsNavigationItem {
  label: string
  description: string
  icon: string
  to: string
  minAccessLevel?: number
}

interface AmsMissionResult {
  id: string | number
  title?: string | null
  mission_type?: string | null
  planned_date?: string | null
  status?: string | null
}

const AMS_NAVIGATION: AmsNavigationItem[] = [
  { label: 'Dashboard', description: 'AMS-Übersicht öffnen', icon: 'i-lucide-layout-grid', to: '/ams' },
  { label: 'Mein Profil', description: 'Persönliche Daten und Ausweis', icon: 'i-lucide-user', to: '/ams/profile' },
  { label: 'Mein Hangar', description: 'Eigene Schiffe und Fahrzeuge', icon: 'i-fluent-home-garage-24-regular', to: '/ams/hangar' },
  { label: 'Comm-Link', description: 'Beiträge und Mitteilungen verwalten', icon: 'i-lucide-newspaper', to: '/ams/comm-link', minAccessLevel: 2 },
  { label: 'Nachrichten', description: 'AMS-Nachrichten öffnen', icon: 'i-lucide-message-square', to: '/ams/messages' },
  { label: 'Mitarbeiter', description: 'ArisCorp-Mitglieder durchsuchen', icon: 'i-lucide-users-round', to: '/ams/employees' },
  { label: 'Flotte', description: 'ArisCorp-Flotte öffnen', icon: 'i-material-symbols-transportation-outline', to: '/ams/fleet' },
  { label: 'Kalender', description: 'Termine und Veranstaltungen', icon: 'i-lucide-calendar-range', to: '/ams/calendar' },
  { label: 'Verwaltung', description: 'AMS administrieren', icon: 'i-lucide-shield-check', to: '/ams/admin', minAccessLevel: 5 },
  { label: 'Missions Board', description: 'Geplante Operationen', icon: 'i-lucide-rocket', to: '/ams/missions' },
  { label: 'Mission erstellen', description: 'Eine neue Operation planen', icon: 'i-lucide-plus-circle', to: '/ams/missions/create', minAccessLevel: 2 },
  { label: 'Anteilsrechner', description: 'Einnahmen und Anteile berechnen', icon: 'i-lucide-calculator', to: '/ams/calculator' },
  { label: 'Inventory (LIM)', description: 'Logistik-Inventar verwalten', icon: 'i-lucide-boxes', to: '/ams/departments/logistics/lim' },
  { label: 'Toolbox', description: 'AMS-Werkzeuge öffnen', icon: 'i-lucide-tool-case', to: '/ams/toolbox', minAccessLevel: 1 },
  { label: 'Bingo', description: 'Bingo in der Toolbox öffnen', icon: 'i-lucide-grid-3x3', to: '/ams/toolbox/bingo', minAccessLevel: 1 },
  { label: 'Versionsübersicht', description: 'Änderungen am AMS ansehen', icon: 'i-lucide-tag', to: '/ams/versions' },
]

const MISSION_TYPES: Record<string, string> = {
  mining: 'Bergbau',
  combat: 'Kampf',
  cargo: 'Fracht',
  exploration: 'Erkundung',
  rescue: 'Rettung',
  patrol: 'Patrouille',
  event: 'Event',
  other: 'Sonstiges',
}

const MISSION_STATUS: Record<string, string> = {
  draft: 'Entwurf',
  active: 'Aktiv',
  cancelled: 'Abgebrochen',
  completed: 'Abgeschlossen',
  archived: 'Archiviert',
}

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const { open, closeCommandPalette } = useCommandPalette()
const searchTerm = ref('')
const vehicles = ref<GameVehicleSummary[]>([])
const employees = ref<DirectusUser[]>([])
const missions = ref<AmsMissionResult[]>([])
const loadingContext = ref<'ams' | 'shipexkurs' | null>(null)
const failedContexts = reactive(new Set<'ams' | 'shipexkurs'>())
const loadedContexts = reactive(new Set<'ams' | 'shipexkurs'>())

const isAmsContext = computed(() => route.path === '/ams' || route.path.startsWith('/ams/'))
const isShipContext = computed(() => route.path === '/shipexkurs' || route.path.startsWith('/shipexkurs/'))

const paletteTitle = computed(() => {
  if (isAmsContext.value) return 'AMS durchsuchen'
  if (isShipContext.value) return 'ShipExkurs durchsuchen'
  return 'ArisCorp durchsuchen'
})

const paletteDescription = computed(() => {
  if (isAmsContext.value) return 'Seiten, Mitarbeiter und Missionen schnell öffnen.'
  if (isShipContext.value) return 'Schiffe nach Name, Hersteller, Rolle oder Größe finden.'
  return 'Zu einem Bereich der ArisCorp-Anwendung wechseln.'
})

const palettePlaceholder = computed(() => {
  if (isAmsContext.value) return 'AMS-Seite, Mitarbeiter oder Mission suchen…'
  if (isShipContext.value) return 'Schiff, Hersteller, Rolle oder Größe suchen…'
  return 'Bereich suchen…'
})

const paletteIcon = computed(() =>
  isShipContext.value ? 'i-lucide-rocket' : isAmsContext.value ? 'i-lucide-panels-top-left' : 'i-lucide-search',
)

const currentAccessLevel = computed(() => Number(authStore.currentUserAL ?? 0))

function routeExists(to: string): boolean {
  return router.resolve(to).matched.length > 0
}

function canOpenAmsRoute(item: AmsNavigationItem): boolean {
  if (!routeExists(item.to)) return false

  const routeAccessLevel = Number(router.resolve(item.to).meta.access_level ?? 0)
  return currentAccessLevel.value >= Math.max(item.minAccessLevel ?? 0, routeAccessLevel)
}

function formatEmployeeName(employee: DirectusUser): string {
  return [employee.title, employee.first_name, employee.middle_name, employee.last_name]
    .filter(Boolean)
    .join(' ')
}

function relationLabel(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null
  const relation = value as Record<string, unknown>
  const label = relation.label ?? relation.name
  return typeof label === 'string' && label.trim() ? label : null
}

function employeeDescription(employee: DirectusUser): string {
  const department = relationLabel(employee.primary_department)
  const role = relationLabel(employee.role)
  return [department, role].filter(Boolean).join(' · ') || 'ArisCorp-Mitarbeiter'
}

function missionDescription(mission: AmsMissionResult): string {
  const parts = [
    mission.mission_type ? (MISSION_TYPES[mission.mission_type] ?? mission.mission_type) : null,
    mission.status ? (MISSION_STATUS[mission.status] ?? mission.status) : null,
  ]

  if (mission.planned_date) {
    const date = new Date(mission.planned_date)
    if (!Number.isNaN(date.getTime())) {
      parts.push(new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' }).format(date))
    }
  }

  return parts.filter(Boolean).join(' · ')
}

function shipDescription(vehicle: GameVehicleSummary): string {
  return [
    vehicle.manufacturer.name,
    vehicle.role ?? vehicle.career,
    vehicle.sizeLabel,
  ].filter(Boolean).join(' · ')
}

const areaItems = computed<CommandPaletteItem[]>(() => [
  {
    label: 'AMS',
    description: 'ArisCorp Management System',
    icon: 'i-lucide-panels-top-left',
    to: '/ams',
    active: isAmsContext.value,
  },
  {
    label: 'ShipExkurs',
    description: 'Schiffskatalog und technische Daten',
    icon: 'i-lucide-rocket',
    to: '/shipexkurs',
    active: isShipContext.value,
  },
])

const amsNavigationItems = computed<CommandPaletteItem[]>(() =>
  AMS_NAVIGATION.filter(canOpenAmsRoute).map((item) => ({
    label: item.label,
    description: item.description,
    icon: item.icon,
    to: item.to,
    active: item.to === '/ams' ? route.path === '/ams' : route.path.startsWith(item.to),
  })),
)

const employeeItems = computed<CommandPaletteItem[]>(() =>
  employees.value.map((employee) => {
    const label = formatEmployeeName(employee)
    const portrait = getAssetId(employee.avatar)

    return {
      label,
      description: employeeDescription(employee),
      ...(portrait
        ? { avatar: { src: portrait, alt: label } }
        : { icon: 'i-lucide-user-round' }),
      suffix: typeof employee.id === 'string' ? employee.id.slice(0, 8) : undefined,
      to: `/ams/employees/${employee.id}`,
      active: route.path === `/ams/employees/${employee.id}`,
    }
  }),
)

const missionItems = computed<CommandPaletteItem[]>(() =>
  missions.value.map((mission) => ({
    label: mission.title?.trim() || `Mission ${mission.id}`,
    description: missionDescription(mission),
    icon: 'i-lucide-orbit',
    to: `/ams/missions/${mission.id}`,
    active: route.path === `/ams/missions/${mission.id}`,
  })),
)

const shipItems = computed<CommandPaletteItem[]>(() =>
  vehicles.value.map((vehicle) => {
    const manufacturerLogo = getAssetId(vehicle.manufacturer.logo)

    return {
      label: vehicle.name,
      suffix: vehicle.productionStatus ?? undefined,
      description: shipDescription(vehicle),
      ...(manufacturerLogo
        ? {
            avatar: {
              src: manufacturerLogo,
              alt: vehicle.manufacturer.name ?? vehicle.manufacturer.short,
              ui: {
                root: 'rounded-none bg-transparent',
                image: 'rounded-none object-contain p-0.5',
              },
            },
          }
        : { icon: vehicle.isGroundVehicle ? 'i-lucide-truck' : 'i-lucide-rocket' }),
      to: `/shipexkurs/ships/${vehicle.id}`,
      active: route.params.id === vehicle.id,
    }
  }),
)

const groups = computed<CommandPaletteGroup[]>(() => {
  if (isAmsContext.value) {
    const result: CommandPaletteGroup[] = [
      { id: 'ams-navigation', label: 'AMS', items: amsNavigationItems.value },
    ]

    if (employeeItems.value.length) {
      result.push({ id: 'ams-employees', label: 'Mitarbeiter', items: employeeItems.value })
    }
    if (missionItems.value.length) {
      result.push({ id: 'ams-missions', label: 'Missionen', items: missionItems.value })
    }
    if (failedContexts.has('ams')) {
      result.push({
        id: 'ams-load-error',
        items: [{
          label: 'AMS-Inhalte konnten nicht vollständig geladen werden',
          description: 'Die Seitennavigation steht weiterhin zur Verfügung.',
          icon: 'i-lucide-triangle-alert',
          disabled: true,
        }],
      })
    }
    result.push({ id: 'areas', label: 'Weitere Bereiche', items: areaItems.value })
    return result
  }

  if (isShipContext.value) {
    const result: CommandPaletteGroup[] = [
      {
        id: 'shipexkurs-navigation',
        label: 'ShipExkurs',
        items: [{
          label: 'Alle Schiffe',
          description: 'Zurück zum vollständigen Schiffskatalog',
          icon: 'i-lucide-layout-grid',
          to: '/shipexkurs',
          active: route.path === '/shipexkurs',
        }],
      },
    ]

    if (shipItems.value.length) {
      result.push({ id: 'shipexkurs-ships', label: 'Schiffe und Fahrzeuge', items: shipItems.value })
    }
    if (failedContexts.has('shipexkurs')) {
      result.push({
        id: 'shipexkurs-load-error',
        items: [{
          label: 'Schiffskatalog konnte nicht geladen werden',
          description: 'Versuche es beim nächsten Öffnen erneut.',
          icon: 'i-lucide-triangle-alert',
          disabled: true,
        }],
      })
    }
    result.push({ id: 'areas', label: 'Weitere Bereiche', items: areaItems.value })
    return result
  }

  return [{ id: 'areas', label: 'Bereiche', items: areaItems.value }]
})

async function loadShipContext() {
  if (loadedContexts.has('shipexkurs') || loadingContext.value === 'shipexkurs') return
  loadingContext.value = 'shipexkurs'
  failedContexts.delete('shipexkurs')

  try {
    vehicles.value = await fetchVehicleIndex()
    loadedContexts.add('shipexkurs')
  } catch (error) {
    failedContexts.add('shipexkurs')
    console.error('Command Palette: Schiffskatalog konnte nicht geladen werden.', error)
  } finally {
    loadingContext.value = null
  }
}

async function loadAmsContext() {
  if (loadedContexts.has('ams') || loadingContext.value === 'ams' || !authStore.isUserLoggedIn) return
  loadingContext.value = 'ams'
  failedContexts.delete('ams')

  const [employeeResult, missionResult] = await Promise.allSettled([
    useDirectus(readUsers({
      filter: {
        status: { _eq: 'active' },
        api_account: { _eq: false },
        hidden: { _eq: false },
      },
      fields: [
        'id',
        'title',
        'first_name',
        'middle_name',
        'last_name',
        'avatar',
        { primary_department: ['name'] },
        { role: ['label', 'name'] },
      ],
      sort: ['first_name'],
      limit: -1,
    })),
    useDirectus(readItems('ams_missions', {
      filter: { status: { _neq: 'archived' } },
      fields: ['id', 'title', 'mission_type', 'planned_date', 'status'],
      sort: ['planned_date'],
      limit: -1,
    })),
  ])

  if (employeeResult.status === 'fulfilled') {
    employees.value = employeeResult.value as DirectusUser[]
  }
  if (missionResult.status === 'fulfilled') {
    missions.value = missionResult.value as AmsMissionResult[]
  }

  if (employeeResult.status === 'rejected' || missionResult.status === 'rejected') {
    failedContexts.add('ams')
    console.error('Command Palette: AMS-Inhalte konnten nicht vollständig geladen werden.', {
      employees: employeeResult.status === 'rejected' ? employeeResult.reason : undefined,
      missions: missionResult.status === 'rejected' ? missionResult.reason : undefined,
    })
  } else {
    loadedContexts.add('ams')
  }

  loadingContext.value = null
}

async function loadCurrentContext() {
  if (isShipContext.value) await loadShipContext()
  if (isAmsContext.value) await loadAmsContext()
}

watch(open, (isOpen) => {
  if (isOpen) {
    void loadCurrentContext()
  } else {
    searchTerm.value = ''
  }
})

watch(
  () => route.fullPath,
  () => closeCommandPalette(),
)

watch(
  () => authStore.isUserLoggedIn,
  (isLoggedIn) => {
    if (isLoggedIn && open.value && isAmsContext.value) void loadAmsContext()
  },
)
</script>

<template>
  <UDashboardSearch
    v-model:open="open"
    v-model:search-term="searchTerm"
    :color-mode="false"
    :description="paletteDescription"
    :fuse="{
      fuseOptions: {
        ignoreLocation: true,
        threshold: 0.25,
        keys: ['label', 'description', 'suffix'],
        useTokenSearch: true,
      },
      matchAllWhenSearchEmpty: true,
      resultLimit: 30,
    }"
    :groups="groups"
    :icon="paletteIcon"
    :loading="loadingContext !== null"
    :placeholder="palettePlaceholder"
    :title="paletteTitle"
    close
    preserve-group-order
    :search-delay="75"
    shortcut="meta_k"
    :ui="{ modal: 'sm:max-w-2xl' }"
  >
    <template #empty="{ searchTerm: emptySearchTerm }">
      <div class="flex flex-col items-center gap-2 px-6 py-10 text-center text-sm text-muted">
        <UIcon name="i-lucide-search-x" class="size-6" />
        <span>Keine Ergebnisse für „{{ emptySearchTerm }}“.</span>
      </div>
    </template>

    <template #footer>
      <div class="flex items-center justify-between gap-4 border-t border-default px-4 py-2 text-xs text-muted">
        <span>{{ isAmsContext ? 'AMS' : isShipContext ? 'ShipExkurs' : 'ArisCorp' }}</span>
        <span class="flex items-center gap-1.5">
          <UKbd value="enter" variant="subtle" /> öffnen
          <UKbd value="escape" variant="subtle" class="ms-2" /> schließen
        </span>
      </div>
    </template>
  </UDashboardSearch>
</template>
